import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useApp } from '@/components/AppLayout';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { paymentApi, clientApi, type Payment, type Client, type PaymentMethod } from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import {
  ArrowLeft,
  ChevronRight,
  Calendar,
  Receipt,
  CreditCard,
  Building2,
  Banknote,
  Smartphone,
  FileText,
  Coins,
  CircleCheck,
  Clock,
  XCircle,
  Trash2,
  Hash,
  StickyNote,
  CircleDollarSign,
} from 'lucide-react';
import { toast } from 'sonner';

// Payment method icon mapping
const getPaymentMethodIcon = (method: PaymentMethod) => {
  switch (method) {
    case 'check':
      return <Receipt className="h-5 w-5" />;
    case 'card':
      return <CreditCard className="h-5 w-5" />;
    case 'ach':
      return <Building2 className="h-5 w-5" />;
    case 'cash':
      return <Banknote className="h-5 w-5" />;
    case 'cash_app':
    case 'paypal':
    case 'venmo':
    case 'zelle':
      return <Smartphone className="h-5 w-5" />;
    case 'credit_memo':
      return <FileText className="h-5 w-5" />;
    case 'stripe':
      return <CreditCard className="h-5 w-5" />;
    case 'other':
    default:
      return <Coins className="h-5 w-5" />;
  }
};

// Payment method display labels
const paymentMethodLabels: Record<PaymentMethod, string> = {
  check: 'Check',
  card: 'Card',
  ach: 'ACH',
  cash: 'Cash',
  cash_app: 'Cash App',
  paypal: 'PayPal',
  venmo: 'Venmo',
  zelle: 'Zelle',
  credit_memo: 'Credit Memo',
  stripe: 'Stripe',
  other: 'Other',
};

export function ViewPayment() {
  const { id } = useParams<{ id: string }>();
  const { nav } = useBusinessNav();
  const { currentBusiness } = useApp();
  const businessId = currentBusiness?.id || '';

  // Data state
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [error, setError] = useState('');

  // UI state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Load payment
  useEffect(() => {
    if (businessId && id) {
      paymentApi.get(id).then((r) => {
        if (r.data) {
          setPayment(r.data);
          // Load client details
          if (r.data.client) {
            clientApi.get(r.data.client).then((cr) => {
              if (cr.data) setClient(cr.data);
            });
          }
          setLoading(false);
        } else if (r.error) {
          setError(r.error);
          setLoading(false);
        }
      });
    }
  }, [businessId, id]);

  const handleDelete = async () => {
    if (!id || !payment) return;
    setDeleting(true);

    const response = await paymentApi.delete(id);
    if (response.error) {
      toast.error(response.error);
      setDeleting(false);
    } else {
      toast.success('Payment deleted');
      nav('/payments');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return null;
  }

  // Error state
  if (error || !payment) {
    return (
      <div className="fixed inset-0 z-50 bg-background">
        <div className="flex flex-col items-center justify-center h-full">
          <div className="text-center">
            <p className="text-destructive mb-4">{error || 'Payment not found'}</p>
            <Button variant="outline" onClick={() => nav('/payments')}>
              Back to Payments
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const unallocatedAmount = parseFloat(payment.unallocated_amount);
  const totalAmount = parseFloat(payment.amount);
  const isFullyAllocated = unallocatedAmount === 0;
  const isCreditMemoPayment = payment.payment_method === 'credit_memo';
  const isStripePayment = payment.payment_method === 'stripe' || !!payment.stripe_payment_id;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="flex-1 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 flex-shrink-0">
              <button
                onClick={() => nav('/payments')}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Payments
              </button>
              {client && (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <button
                    onClick={() => nav(`/clients/${client.id}`)}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {client.billing_name}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
              <div className="flex-1" />
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-8 pb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="max-w-3xl mx-auto space-y-6"
              >
                {/* Title - Payment Amount */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <CircleDollarSign className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-semibold text-green-600">
                        {formatAmount(payment.amount)}
                      </h1>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          {getPaymentMethodIcon(payment.payment_method)}
                          <span>{paymentMethodLabels[payment.payment_method]}</span>
                        </div>
                        <span className="text-muted-foreground/50">·</span>
                        <div className="flex items-center gap-1.5">
                          {payment.status === 'completed' && (
                            <>
                              <CircleCheck className="h-4 w-4 text-green-600" />
                              <span className="text-green-600 text-sm">Completed</span>
                            </>
                          )}
                          {payment.status === 'pending' && (
                            <>
                              <Clock className="h-4 w-4 text-amber-600" />
                              <span className="text-amber-600 text-sm">Processing</span>
                            </>
                          )}
                          {payment.status === 'failed' && (
                            <>
                              <XCircle className="h-4 w-4 text-red-600" />
                              <span className="text-red-600 text-sm">Failed</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Details */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="space-y-4"
                >
                  <Label className="text-muted-foreground">Payment Details</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Payment Date</Label>
                      <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{formatDate(payment.payment_date)}</span>
                      </div>
                    </div>
                    {payment.reference && (
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Reference</Label>
                        <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                          <Hash className="h-4 w-4 text-muted-foreground" />
                          <span>{payment.reference}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {payment.notes && (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Notes</Label>
                      <div className="flex items-start gap-2 p-3 border rounded-lg bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900">
                        <StickyNote className="h-4 w-4 text-amber-600 mt-0.5" />
                        <span className="whitespace-pre-wrap">{payment.notes}</span>
                      </div>
                    </div>
                  )}
                </motion.div>

                {/* Source Section - Credit Memo or Stripe */}
                {isCreditMemoPayment && payment.credit_memo && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-3"
                  >
                    <Label className="text-muted-foreground">Source</Label>
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <span>Created from Credit Memo</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => nav(`/credit-memos/${payment.credit_memo}`)}
                      >
                        View Credit Memo
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Allocations */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="space-y-3"
                >
                  <div className="flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-muted-foreground" />
                    <Label className="text-base font-medium">
                      Applied To
                      {payment.allocations.length > 0 && (
                        <span className="text-muted-foreground font-normal ml-2">
                          ({payment.allocations.length} {payment.allocations.length === 1 ? 'document' : 'documents'})
                        </span>
                      )}
                    </Label>
                  </div>

                  {payment.allocations.length > 0 ? (
                    <div className="border rounded-lg divide-y">
                      {payment.allocations.map((alloc) => (
                        <div
                          key={alloc.id}
                          className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 cursor-pointer transition-colors"
                          onClick={() => {
                            if (alloc.invoice) {
                              nav(`/invoices/${alloc.invoice}`);
                            } else if (alloc.quote) {
                              nav(`/quotes/${alloc.quote}`);
                            }
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                              {alloc.invoice ? (
                                <FileText className="h-4 w-4 text-blue-600" />
                              ) : (
                                <Receipt className="h-4 w-4 text-purple-600" />
                              )}
                            </div>
                            <div>
                              <div className="font-medium">
                                {alloc.invoice ? `Invoice #${alloc.invoice_number}` : `Quote #${alloc.quote_number}`}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {alloc.invoice ? 'Invoice' : 'Quote Deposit'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-green-600">
                              {formatAmount(alloc.amount)}
                            </span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border rounded-lg p-8 text-center">
                      <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                        <Clock className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="text-muted-foreground">
                        This payment hasn't been applied to any invoices or quotes yet.
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        The full amount is available as client credit.
                      </p>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Sidebar */}
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring', damping: 25 }}
            className="w-72 border-l bg-muted/30 flex flex-col flex-shrink-0"
          >
            {/* Payment Status Header */}
            <div className="px-5 py-4 border-b">
              <div className="text-sm text-muted-foreground">Payment Status</div>
              <div className="flex items-center gap-2 mt-1">
                {payment.status === 'completed' && (
                  <>
                    <CircleCheck className="h-5 w-5 text-green-600" />
                    <span className="text-lg font-semibold text-green-600">Completed</span>
                  </>
                )}
                {payment.status === 'pending' && (
                  <>
                    <Clock className="h-5 w-5 text-amber-600" />
                    <span className="text-lg font-semibold text-amber-600">Processing</span>
                  </>
                )}
                {payment.status === 'failed' && (
                  <>
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="text-lg font-semibold text-red-600">Failed</span>
                  </>
                )}
              </div>
              {/* Allocation status for completed payments */}
              {payment.status === 'completed' && !isFullyAllocated && (
                <div className="mt-2 text-sm text-amber-600">
                  {formatAmount(payment.unallocated_amount)} unallocated
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 pb-5 pt-4 space-y-5">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Details</h2>

              {/* Payment Method */}
              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs">Payment Method</Label>
                <div className="flex items-center gap-2">
                  {getPaymentMethodIcon(payment.payment_method)}
                  <span>{paymentMethodLabels[payment.payment_method]}</span>
                </div>
              </div>

              {/* Date Received */}
              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs">Date Received</Label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{formatDate(payment.payment_date)}</span>
                </div>
              </div>

              {/* Reference */}
              {payment.reference && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs">Reference</Label>
                  <span className="text-sm">{payment.reference}</span>
                </div>
              )}

              {/* Total Amount */}
              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs">Total Amount</Label>
                <span className="text-lg font-semibold">{formatAmount(payment.amount)}</span>
              </div>

              {/* Allocation Breakdown */}
              {!isFullyAllocated && payment.allocations.length > 0 && (
                <div className="space-y-2 pt-2 border-t">
                  <Label className="text-muted-foreground text-xs">Breakdown</Label>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Applied</span>
                      <span className="text-green-600">
                        {formatAmount((totalAmount - unallocatedAmount).toFixed(2))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Unallocated</span>
                      <span className="text-amber-600">
                        {formatAmount(payment.unallocated_amount)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Fixed action buttons at bottom */}
            <div className="p-4 border-t bg-background space-y-3">
              {!isCreditMemoPayment && !isStripePayment && (
                <Button
                  variant="outline"
                  className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Payment
                </Button>
              )}
              {isCreditMemoPayment && (
                <p className="text-xs text-muted-foreground text-center">
                  To delete this payment, delete the source credit memo.
                </p>
              )}
              {isStripePayment && (
                <p className="text-xs text-muted-foreground text-center">
                  Stripe payments cannot be deleted. Contact support for refunds.
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this payment? This will remove all allocations and
              may affect invoice and quote statuses.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
